import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import BrandingEditorLockService from '@services/BrandingEditorLockService';
import {
  IBrandingEditorLock,
  IBrandingEditorLockState,
} from '@interfaces/brandingEditorLock';
import { IParticipant } from '@interfaces/participants';

export interface IUseBrandingEditorLock {
  /** Latest state this hub knows, or `null` before the first read. */
  lockState: IBrandingEditorLockState | null;
  /** The live lock, if anybody holds one. */
  lock: IBrandingEditorLock | null;
  /** Somebody other than this participant is editing. */
  isLockedByOther: boolean;
  /** Who is editing, for the UI — see `resolveLockHolderName`. */
  holderName: string;
  /**
   * Takes the lock for this hub session. Shows the refusal as a notification
   * and resolves `false` when somebody else holds it or the call failed.
   */
  acquire: () => Promise<boolean>;
  /** Re-reads the lock from the backend into the shared state. */
  refresh: () => Promise<void>;
}

/**
 * Names the holder of a lock for the UI.
 *
 * A name where one exists, else an email, else the id — never a generic
 * "another user": an admin looking at a locked button needs to know whom to
 * ask, and every participant has at least an id.
 *
 * @param lock - The lock as the backend reported it.
 * @param participants - The experience's participant list, for a holder the
 * lock itself does not name.
 * @returns A non-empty label.
 */
export const resolveLockHolderName = (
  lock: IBrandingEditorLock,
  participants: IParticipant[],
): string => {
  if (lock.participantName) {
    return lock.participantName;
  }

  const holder = participants.find(
    (participant) => participant.participantId === lock.participantId,
  );

  return (
    holder?.fullName ||
    holder?.name ||
    lock.participantEmail ||
    holder?.email ||
    lock.participantId
  );
};

/**
 * The experience's exclusive Editor Mode lock, as seen from this hub.
 *
 * The state itself lives in `MytaverseEventProvider` so the Admin Tools button
 * (top bar) and the lifecycle bridge (event page) read the same value; this
 * hook adds the derived flags and the two calls that change it from the UI.
 * Heartbeats and releases are the bridge's job — see
 * `useBrandingEditorLockLifecycle`.
 *
 * @returns See `IUseBrandingEditorLock`.
 */
export const useBrandingEditorLock = (): IUseBrandingEditorLock => {
  const { t: translate } = useTranslation();
  const { showNotification } = useNotification();
  const { websocketSessionId } = useMytaverse();
  const {
    currentEventId,
    currentParticipantId,
    participants,
    brandingEditorLockState,
    setBrandingEditorLockState,
  } = useMytaverseEvent();

  const lock = brandingEditorLockState?.lock ?? null;

  // The same participant in another tab is not "somebody else": the backend
  // lets them re-take their own lock, and the old tab learns on its heartbeat.
  const isLockedByOther =
    !!lock &&
    !!currentParticipantId &&
    lock.participantId !== currentParticipantId;

  const holderName = useMemo(
    () => (lock ? resolveLockHolderName(lock, participants) : ''),
    [lock, participants],
  );

  const refresh = useCallback(async () => {
    if (!currentEventId) {
      return;
    }

    try {
      const state = await BrandingEditorLockService.getLock(currentEventId);
      setBrandingEditorLockState(state);
    } catch (error) {
      // A failed read keeps the last known state: the broadcast and the next
      // poll correct it, and the acquire call is the one that decides anyway.
      console.error(
        `Failed to read the branding editor lock: ${(error as Error)?.message}`,
        { eventId: currentEventId },
      );
    }
  }, [currentEventId, setBrandingEditorLockState]);

  const acquire = useCallback(async () => {
    if (!currentEventId || !websocketSessionId) {
      return false;
    }

    try {
      const { held, state } = await BrandingEditorLockService.acquireLock(
        currentEventId,
        websocketSessionId,
      );
      setBrandingEditorLockState(state);

      if (!held) {
        showNotification({
          message: translate(
            'adminToolbox.editorModeLockedNotification',
            'Only one user can be in Editor Mode at a time. {{name}} is editing this experience right now.',
            {
              name: state.lock
                ? resolveLockHolderName(state.lock, participants)
                : '',
            },
          ),
          type: NOTIFICATION_TYPES.WARNING,
        });
      }

      return held;
    } catch (error) {
      console.error(
        `Failed to acquire the branding editor lock: ${
          (error as Error)?.message
        }`,
        { eventId: currentEventId },
      );

      showNotification({
        message: translate(
          'adminToolbox.editorModeLockCheckFailed',
          'Could not check who is editing this experience. Please try again.',
        ),
        type: NOTIFICATION_TYPES.ERROR,
      });

      return false;
    }
  }, [
    currentEventId,
    websocketSessionId,
    participants,
    setBrandingEditorLockState,
    showNotification,
    translate,
  ]);

  return {
    lockState: brandingEditorLockState,
    lock,
    isLockedByOther,
    holderName,
    acquire,
    refresh,
  };
};
